import { useState, useEffect } from "react";
import { AudioLines, Monitor, Smartphone, ArrowDown, X, Users, Briefcase, Navigation } from "lucide-react";
import CommunityGrant from "./CommunityGrant";
import "./App.css";
import struggleimg from './struggle.webp';
import FounderPic from './Adam.jpg';
import ConfRoom from './NodeConference.jpg';
import NodeOffice from './NodeOffice.jpg';
import TherapyRoom from './NodeTherapyRoom.jpg';
import Connect from './Connect.jpg';
import Insulation from './Insulation.jpg';
import Access from './Access.jpg';

const GOOGLE_FORM_URL =
  "https://script.google.com/macros/s/AKfycbwG7kB6pX13DDxT1bxXse7QFj1w8XS3xms19OU2bmLQjzAhZw6FtYonHVT-GRyeSYE5vA/exec";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/898197df-9c91-4209-a124-87bee743dd54/images/62748bcaef722679b5f8870b6be09f11c5d7c834c5ebb20efb8c46c5488ca958.png";
const STRUGGLE_IMG = struggleimg
// "https://images.unsplash.com/photo-1762781485364-aefd59b44eb1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxjb21tdXRlciUyMHRyYWluJTIwd2luZG93JTIwYmx1cnxlbnwwfHx8fDE3NzU1ODMzOTF8MA&ixlib=rb-4.1.0&q=85";
const FOUNDER_IMG = FounderPic
// "https://images.pexels.com/photos/10657877/pexels-photo-10657877.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CONFERENCE_ROOM_IMG = ConfRoom
const OFFICE_ROOM_IMG = NodeOffice
const THERAPY_ROOM_IMG = TherapyRoom
const CONNECT_IMG = Connect
const INSULATION_IMG = Insulation
const ACCESS_IMG = Access

const FEATURES = [
  {
    icon: AudioLines,
    title: "Acoustic Privacy",
    body: "Engineered soundproofing ensures peace and quiet, and complete confidentiality for your most important meetings.",
    testid: "feature-privacy",
  },
  {
    icon: Monitor,
    title: "Plug & Play Tech",
    body: 'Pre-wired widescreen 4K monitors, webcams, dedicated fiber internet, and a conference room with AI-powered video bar. Just plug in your laptop.',
    testid: "feature-tech",
  },
  {
    icon: Smartphone,
    title: "Zero Friction Access",
    body: "Nearby location. No front desk, no waiting. Your secure digital key activates the moment your booking begins. Walk right in and unlock productivity.",
    testid: "feature-access",
  },
];

export default function App() {
  const [currentPage, setCurrentPage] = useState(
    window.location.pathname.includes("community") ? "community" : "home"
  );
  // Modal State: null | 'privacy' | 'terms'
  const [activeModal, setActiveModal] = useState(null);
  const [email, setEmail] = useState("");
  const [source, setSource] = useState("");
  const [referredBy, setReferredBy] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // History listener for back button
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPage(window.location.pathname.includes("community") ? "community" : "home");
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Scroll listener for nav bar
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when a modal is open for a premium feel
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [activeModal]);

  const navigate = (page, path) => {
    window.history.pushState({}, "", path);
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      // 1. Switch to URLSearchParams so Google Apps Script can read it natively
      const params = new URLSearchParams();
      params.append("email", email);
      params.append("requestType", "Waitlist"); // <-- THIS is what fills in Column C!
      params.append("source", source);
      params.append("referredBy", referredBy);

      // 2. Send the URL Encoded data
      await fetch(GOOGLE_FORM_URL, {
        method: "POST",
        body: params,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        mode: "no-cors",
      });
    } catch (_) {
    } finally {
      setSubmitted(true);
      setSubmitting(false);
      setEmail("");
    }
  };

  return (
    <div className="bg-[#0A0A0C] text-white antialiased overflow-x-hidden relative">

      {/* ── Sticky Glassmorphism Nav ── */}
      <nav
        data-testid="main-nav"
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${scrolled
          ? "bg-[#0A0A0C]/80 backdrop-blur-2xl border-b border-white/[0.08]"
          : "bg-transparent"
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-16 flex items-center justify-between">
          <span
            onClick={() => navigate('home', '/')}
            className="text-base font-semibold tracking-[0.25em] text-white uppercase select-none cursor-pointer hover:text-[#D4AF37] transition-colors"
          >
            Node
          </span>
          <button
            onClick={() => {
              navigate('home', '/');
              setTimeout(() => document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' }), 100);
            }}
            data-testid="nav-cta"
            className="hidden sm:inline-flex items-center gap-2 px-5 py-2 text-xs font-semibold uppercase tracking-[0.1em] border border-[#D4AF37]/50 text-[#D4AF37] rounded-full hover:bg-[#D4AF37]/10 transition-all duration-300"
          >
            Join the Waitlist
          </button>
        </div>
      </nav>

      {/* ── Conditional Rendering ── */}
      {currentPage === 'home' ? (
        <>
          {/* ── Section 1: Hero ── */}
          <section
            id="hero"
            data-testid="hero-section"
            className="relative min-h-screen flex items-center overflow-hidden"
          >
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
              style={{ backgroundImage: `url(${HERO_BG})` }}
            />
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-[#0A0A0C]" />

            <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 pt-24 pb-32">
              <p className="hero-overline text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-6">
                Somers / Yorktown Corridor
              </p>
              <h1 className="hero-headline text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95] text-white mb-8 max-w-3xl">
                Your Executive
                <br />
                Sanctuary.{" "}
                <span className="text-[#D4AF37]">By the Hour.</span>
              </h1>
              <p className="hero-sub text-base sm:text-lg font-light text-slate-300 max-w-xl leading-relaxed mb-10">
                Acoustically secure, hyper-connected micro-offices. <strong className="text-white font-medium">Targeting a Late 2026 launch in Somers/Yorktown.</strong> Join the waitlist to help fast-track the build-out and instantly unlock 50% off your first booking.
              </p>
              <div className="hero-cta">
                <a
                  href="#waitlist"
                  data-testid="hero-cta-button"
                  className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#0A0A0C] px-8 py-4 rounded-full font-semibold text-sm tracking-wide hover:-translate-y-1 hover:shadow-[0_8px_40px_rgba(212,175,55,0.45)] transition-all duration-300"
                >
                  Join the VIP Waitlist (Get 50% Off First Booking)
                </a>
              </div>
            </div>

            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-50">
              <ArrowDown className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
          </section>

          {/* ── Section 2: The Remote Professional's Dilemma ── */}
          <section
            id="dilemma"
            data-testid="dilemma-section"
            className="py-24 sm:py-32 px-6 sm:px-12 bg-[#0A0A0C]"
          >
            <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 sm:gap-20 items-center">
              <div className="relative overflow-hidden rounded-2xl aspect-[4/5] max-h-[80vh] order-last md:order-first">
                <img
                  src={STRUGGLE_IMG}
                  alt="Frustrated professional struggling to concentrate on work"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/50 to-transparent" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-5">
                  The Remote Professional's Dilemma
                </p>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight leading-tight text-white mb-6">
                  Stop taking high-stakes calls from the kitchen table.
                </h2>
                <p className="text-base leading-relaxed font-light text-slate-300">
                  Escape the chaos of coffee shops and distractions of the
                  home office. Node provides a local, guaranteed professional environment where you can focus and be productive without
                  the long commute.
                </p>
              </div>
            </div>
          </section>

          {/* ── Section 3: Who is Node For? (Target Personas) ── */}
          <section
            id="personas"
            data-testid="personas-section"
            className="py-24 sm:py-32 bg-[#121216] border-y border-white/[0.05]"
          >
            <div className="max-w-7xl mx-auto px-6 sm:px-12">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-4 text-center">
                Who Uses Node?
              </p>
              <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white text-center mb-16">
                Built for the way you actually work.
              </h2>

              <div className="grid md:grid-cols-3 gap-8">
                {/* Persona 1: The Local Team */}
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden hover:bg-white/[0.04] hover:border-[#D4AF37]/30 hover:-translate-y-1 transition-all duration-300 group flex flex-col">
                  {/* IMAGE PLACEHOLDER */}
                  <div className="aspect-[16/9] w-full bg-slate-800 relative overflow-hidden border-b border-white/10">
                    {<img src={CONFERENCE_ROOM_IMG} alt="Conference Room Setup" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                    {/* <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs font-light tracking-widest uppercase">Team Room Image</div> */}
                  </div>
                  <div className="p-8 sm:p-10 flex-1 flex flex-col">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 border border-white/10 mb-8 group-hover:bg-[#D4AF37]/10 group-hover:border-[#D4AF37]/30 transition-all duration-300">
                      <Users className="w-5 h-5 text-slate-300 group-hover:text-[#D4AF37] transition-colors" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-medium tracking-tight text-white mb-4">
                      The Local Team
                    </h3>
                    <p className="text-sm leading-relaxed font-light text-slate-400">
                      Perfect for local agencies, startups, or small businesses that don't want the overhead of a massive permanent lease, but <em className="text-white font-normal not-italic">do</em> periodically need a premium space. Gather your team in our Professional Conference Room for quarterly planning, intense sprint sessions, or high-stakes client pitches.
                    </p>
                  </div>
                </div>

                {/* Persona 2: The Remote Professional */}
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden hover:bg-white/[0.04] hover:border-[#D4AF37]/30 hover:-translate-y-1 transition-all duration-300 group flex flex-col">
                  <div className="aspect-[16/9] w-full bg-slate-800 relative overflow-hidden border-b border-white/10">
                    {<img src={OFFICE_ROOM_IMG} alt="Micro-Office Pod" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                  </div>
                  <div className="p-8 sm:p-10 flex-1 flex flex-col">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 border border-white/10 mb-8 group-hover:bg-[#D4AF37]/10 group-hover:border-[#D4AF37]/30 transition-all duration-300">
                      <Navigation className="w-5 h-5 text-slate-300 group-hover:text-[#D4AF37] transition-colors" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-medium tracking-tight text-white mb-4">
                      The Remote Professional
                    </h3>
                    <p className="text-sm leading-relaxed font-light text-slate-400">
                      Perhaps you're a professional with a hybrid or remote role and you're constantly surrounded by noise and distractions at home. Or maybe you manage a territory spanning Westchester, the Hudson Valley, and Connecticut and you're always on the move. Stop taking your important Zoom calls from your home, stop making deals from the driver's seat, and stop killing time in noisy cafes while you're in between client visits. Node is your on-demand oasis— drop in, connect to dedicated fiber internet, and close the deal in total privacy.
                    </p>
                  </div>
                </div>


                {/* Persona 3: The Practitioner */}
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden hover:bg-white/[0.04] hover:border-[#D4AF37]/30 hover:-translate-y-1 transition-all duration-300 group flex flex-col">
                  <div className="aspect-[16/9] w-full bg-slate-800 relative overflow-hidden border-b border-white/10">
                    {<img src={THERAPY_ROOM_IMG} alt="1-on-1 Office Setup" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                  </div>
                  <div className="p-8 sm:p-10 flex-1 flex flex-col">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 border border-white/10 mb-8 group-hover:bg-[#D4AF37]/10 group-hover:border-[#D4AF37]/30 transition-all duration-300">
                      <Briefcase className="w-5 h-5 text-slate-300 group-hover:text-[#D4AF37] transition-colors" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-medium tracking-tight text-white mb-4">
                      The Practitioner
                    </h3>
                    <p className="text-sm leading-relaxed font-light text-slate-400">
                      Whether you are a private wealth advisor, clinical therapist, executive coach, or specialized tutor - meeting your clients at a coffee shop, library, or other local haunt compromises your professionalism. Our engineered acoustic privacy ensures your client conversations stay strictly in the room, giving you the professional polish you need without the permanent overhead.
                    </p>
                  </div>
                </div>

              </div>
            </div>
          </section>

          {/* ── Section 4: Why Node? (Features & Benefits) ── */}
          <section
            id="features"
            data-testid="features-section"
            className="py-24 sm:py-32 bg-[#0A0A0C]"
          >
            <div className="max-w-7xl mx-auto px-6 sm:px-12">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-4 text-center">
                The Node Advantage
              </p>
              <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white text-center mb-16">
                Engineered for deep work.
              </h2>

              <div className="grid md:grid-cols-3 gap-8">
                {/* Feature 1: Acoustic Privacy */}
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden hover:bg-white/[0.04] hover:border-[#D4AF37]/30 hover:-translate-y-1 transition-all duration-300 group flex flex-col">
                  {/* IMAGE PLACEHOLDER */}
                  <div className="aspect-[16/9] w-full bg-slate-800 relative overflow-hidden border-b border-white/10">
                    {<img src={INSULATION_IMG} alt="Acoustic Wall Paneling" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                    {/* <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs font-light tracking-widest uppercase">Acoustic Details Image</div> */}
                  </div>
                  <div className="p-8 sm:p-10 flex-1 flex flex-col">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 border border-white/10 mb-8 group-hover:bg-[#D4AF37]/10 group-hover:border-[#D4AF37]/30 transition-all duration-300">
                      <AudioLines className="w-5 h-5 text-slate-300 group-hover:text-[#D4AF37] transition-colors" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-medium tracking-tight text-white mb-4">
                      True Acoustic Privacy
                    </h3>
                    <p className="text-sm leading-relaxed font-light text-slate-400">
                      No fishbowls. No thin glass walls. Our offices and conference rooms are engineered with staggered studs, specialized damping compounds, and acoustic felt to ensure your high-stakes conversations stay in the room.
                    </p>
                  </div>
                </div>

                {/* Feature 2: Premium Tech Stack */}
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden hover:bg-white/[0.04] hover:border-[#D4AF37]/30 hover:-translate-y-1 transition-all duration-300 group flex flex-col">
                  {/* IMAGE PLACEHOLDER */}
                  <div className="aspect-[16/9] w-full bg-slate-800 relative overflow-hidden border-b border-white/10">
                    {<img src={CONNECT_IMG} alt="32-inch 4K Hub Monitor" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                    {/* <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs font-light tracking-widest uppercase">Tech Stack Image</div> */}
                  </div>
                  <div className="p-8 sm:p-10 flex-1 flex flex-col">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 border border-white/10 mb-8 group-hover:bg-[#D4AF37]/10 group-hover:border-[#D4AF37]/30 transition-all duration-300">
                      <Monitor className="w-5 h-5 text-slate-300 group-hover:text-[#D4AF37] transition-colors" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-medium tracking-tight text-white mb-4">
                      Plug-and-Play Command
                    </h3>
                    <p className="text-sm leading-relaxed font-light text-slate-400">
                      Stop hunching over a 14-inch laptop screen. Every office features a premium, ergonomic sit-stand desk and a spacious 4K hub monitor. Plug in a single USB-C cable for instant power, video, and HD webcam, and enjoy top-grade high-speed wireless internet.
                    </p>
                  </div>
                </div>

                {/* Feature 3: Frictionless Access */}
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden hover:bg-white/[0.04] hover:border-[#D4AF37]/30 hover:-translate-y-1 transition-all duration-300 group flex flex-col">
                  {/* IMAGE PLACEHOLDER */}
                  <div className="aspect-[16/9] w-full bg-slate-800 relative overflow-hidden border-b border-white/10">
                    {<img src={ACCESS_IMG} alt="Smartphone Door Unlock" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                    {/* <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs font-light tracking-widest uppercase">Smart Lock Image</div> */}
                  </div>
                  <div className="p-8 sm:p-10 flex-1 flex flex-col">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5 border border-white/10 mb-8 group-hover:bg-[#D4AF37]/10 group-hover:border-[#D4AF37]/30 transition-all duration-300">
                      <Smartphone className="w-5 h-5 text-slate-300 group-hover:text-[#D4AF37] transition-colors" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-medium tracking-tight text-white mb-4">
                      Frictionless Access
                    </h3>
                    <p className="text-sm leading-relaxed font-light text-slate-400">
                      No receptionists, no small talk, no waiting. Book your room securely via our web app, and your phone instantly becomes your digital key. Walk in, unlock your sanctuary, and get straight to work.
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* ── Section 5: Pricing ── */}
          <section
            id="pricing"
            data-testid="pricing-section"
            className="py-24 sm:py-32 px-6 sm:px-12 bg-[#0A0A0C]"
          >
            <div className="max-w-5xl mx-auto">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-4 text-center">
                Pricing
              </p>
              <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white text-center mb-16">
                Premium workspace, precisely when you need it.
              </h2>
              <div className="grid sm:grid-cols-2 gap-8">

                {/* Office Space */}
                <div
                  data-testid="pricing-micro-office"
                  className="bg-white/[0.04] backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 hover:-translate-y-1 transition-all duration-300 flex flex-col"
                >
                  {/* UPDATE WITH YOUR AI IMAGE */}
                  <div className="aspect-[16/9] w-full bg-slate-800 relative">
                    {<img src={OFFICE_ROOM_IMG} alt="Micro-Office Conceptual Rendering" className="w-full h-full object-cover" />}
                    {/* <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs font-light tracking-widest uppercase">Image Placeholder</div> */}
                  </div>
                  <div className="p-8 flex-1 flex flex-col">
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#D4AF37] mb-5">
                      Executive Micro-Office
                    </p>
                    <div className="flex items-end gap-2 mb-3">
                      <span className="text-6xl font-light text-white tracking-tighter">
                        $30
                      </span>
                      <span className="text-slate-400 mb-2 font-light text-sm">
                        / hour
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 font-light mb-8 flex-1">
                      Available in 15-minute increments, 1 hour minimum. Features a widescreen hub monitor, webcam, ergonomic seating, and acoustic privacy.
                    </p>
                    <a
                      href="#waitlist"
                      data-testid="pricing-conference-cta"
                      className="block w-full text-center py-3 bg-[#D4AF37] text-[#0A0A0C] rounded-full text-sm font-semibold hover:bg-[#E6C65A] hover:shadow-[0_4px_24px_rgba(212,175,55,0.4)] transition-all duration-300"
                    >
                      Join Waitlist
                    </a>
                  </div>
                </div>

                {/* Conference Room */}
                <div
                  data-testid="pricing-conference"
                  className="bg-white/[0.04] backdrop-blur-2xl border border-[#D4AF37]/30 rounded-2xl shadow-[0_0_50px_rgba(212,175,55,0.07)] hover:border-[#D4AF37]/60 hover:-translate-y-1 transition-all duration-300 relative overflow-hidden flex flex-col"
                >
                  {/* UPDATE WITH YOUR AI IMAGE */}
                  <div className="aspect-[16/9] w-full bg-slate-800 relative">
                    {<img src={CONFERENCE_ROOM_IMG} alt="Conference Room Conceptual Rendering" className="w-full h-full object-cover" />}
                    {/* <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs font-light tracking-widest uppercase">Image Placeholder</div> */}
                  </div>
                  <div className="p-8 flex-1 flex flex-col">
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#D4AF37] mb-5">
                      Professional Conference Room
                    </p>
                    <div className="flex items-end gap-2 mb-3">
                      <span className="text-6xl font-light text-white tracking-tighter">
                        $75
                      </span>
                      <span className="text-slate-400 mb-2 font-light text-sm">
                        / hour
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 font-light mb-8 flex-1">
                      Seats 4–6. Equipped with an AI-powered video conferencing bar, presentation display, and table power hubs.
                    </p>
                    <a
                      href="#waitlist"
                      data-testid="pricing-conference-cta"
                      className="block w-full text-center py-3 bg-[#D4AF37] text-[#0A0A0C] rounded-full text-sm font-semibold hover:bg-[#E6C65A] hover:shadow-[0_4px_24px_rgba(212,175,55,0.4)] transition-all duration-300"
                    >
                      Join Waitlist
                    </a>
                  </div>
                </div>
              </div>

              {/* Context & Disclaimer Text */}
              <div className="mt-8 flex flex-col items-center gap-2">
                <p className="text-center text-sm text-slate-400 font-light">
                  Multi-hour block discounts and corporate wallet reloads available.
                </p>
                <p className="text-center text-xs text-slate-600 font-light italic max-w-2xl">
                  *Images shown are conceptual architectural renderings. Final room finishes, dimensions, and hardware specifications are subject to change during the construction phase.
                </p>
              </div>
            </div>
          </section>

          {/* ── Section 5: Capture + Founder ── */}
          <section
            id="waitlist"
            data-testid="waitlist-section"
            className="py-24 sm:py-32 bg-[#121216]"
          >
            <div className="max-w-7xl mx-auto px-6 sm:px-12 grid md:grid-cols-2 gap-12 sm:gap-20 items-center">

              {/* Left: Founder's Note */}
              <div data-testid="founder-note">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-8">
                  Our Founder
                </p>
                <div className="flex items-center gap-4 mb-8">
                  <img
                    src={FOUNDER_IMG}
                    alt="Adam, Founder of Node Workspaces"
                    data-testid="founder-avatar"
                    className="w-16 h-16 rounded-full object-cover border-2 border-[#D4AF37]/30 object-top"
                  />
                  <div>
                    <p className="text-white font-medium text-sm">Adam</p>
                    <p className="text-slate-400 text-xs font-light mt-0.5">
                      Founder, Node Workspaces
                    </p>
                  </div>
                </div>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white mb-6">
                  Built in our own backyard.
                </h2>
                <blockquote className="text-base leading-relaxed font-light text-slate-300 border-l-2 border-[#D4AF37]/40 pl-6 italic">
                  <p className="mb-6">
                    "As a local professional who has lived in the community for over 20 years, I've recognized a need for having a nearby, quiet place to take a high-stakes call, hold a productive working session with other local co-workers, or simply tune out distractions and be able to focus for a while. We shouldn't have to schlep down-county (or worse, to the City!) to find a professional, technology-equipped working space in a serene environment. That's why I'm building <strong className="text-[#D4AF37] font-extrabold tracking-wide">Node</strong> right here in the Somers/Yorktown area.
                  </p>
                  <p>
                    If this resonates with you, join our early-access waitlist by submitting your email and receive 50% off your first booking once we open. And if you have questions, our email is below - don't hesitate to reach out."
                  </p>
                  <footer className="mt-5 text-[#D4AF37] font-semibold not-italic text-sm tracking-wide">
                    — Adam
                  </footer>
                </blockquote>
              </div>

              {/* Right: Email Capture */}
              <div
                data-testid="email-capture-panel"
                className="bg-[#0A0A0C] rounded-2xl border border-white/[0.08] p-8 sm:p-10"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-5">
                  Early Access
                </p>
                <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-white mb-4">
                  Secure Your Early-Access Rate.
                </h2>
                <p className="text-sm leading-relaxed font-light text-slate-400 mb-8">
                  Targeting a Late 2026 launch in Somers/Yorktown. Drop your email below to help us accelerate the build-out and instantly secure 50% off your first session.
                </p>

                {submitted ? (
                  <div
                    data-testid="form-success"
                    className="text-center py-10 border border-[#D4AF37]/20 rounded-xl bg-[#D4AF37]/[0.04]"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-6 h-6 text-[#D4AF37]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <p className="text-white font-medium mb-1">
                      You're on the list!
                    </p>
                    <p className="text-slate-400 text-sm font-light">
                      We'll be in touch when Node opens its doors.
                    </p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    action={GOOGLE_FORM_URL}
                    method="POST"
                    data-testid="email-capture-form"
                  >
                    {/* --- NEW DROPDOWN START --- */}
                    <div className="mb-3">
                      <select
                        required
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                        className={`w-full bg-transparent border rounded-xl px-5 py-3 text-sm transition-colors duration-300 focus:outline-none focus:border-[#D4AF37]/60 appearance-none cursor-pointer ${
                          source === "" ? "text-slate-500 border-white/20" : "text-white border-white/40"
                        }`}
                      >
                        <option value="" disabled>How did you hear about us?</option>
                        <option value="Local Facebook Group">Local Facebook Group</option>
                        <option value="Facebook/Instagram Ad">Facebook or Instagram Ad</option>
                        <option value="Friend/Referral">Friend or Referral</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* --- CONDITIONAL REFERRED BY FIELD --- */}
                    {source === "Friend/Referral" && (
                      <div className="mb-3 animate-in fade-in slide-in-from-top-2 duration-300">
                        <input
                          type="email"
                          required
                          value={referredBy}
                          onChange={(e) => setReferredBy(e.target.value)}
                          placeholder="Friend's email address"
                          className="w-full bg-transparent border border-white/20 rounded-xl px-5 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors duration-300"
                        />
                      </div>
                    )}
                    {/* --- NEW DROPDOWN END --- */}
                    
                    <div className="flex flex-col sm:flex-row gap-3 mb-4">
                      <input
                        type="email"
                        name="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email address"
                        data-testid="email-input"
                        className="flex-1 min-w-0 bg-transparent border border-white/20 rounded-full px-5 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors duration-300"
                      />
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="email-submit-button"
                        className="bg-[#D4AF37] text-[#0A0A0C] px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-[#E6C65A] hover:-translate-y-0.5 hover:shadow-[0_4px_24px_rgba(212,175,55,0.4)] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {submitting ? "Securing..." : "Secure My 50% Off Credit"}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 font-light leading-relaxed">
                      No spam, ever. Your email is only used for your opening day
                      discount and to keep you posted about our opening. We will never sell your data.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </section>
        </>
      ) : (
        <CommunityGrant />
      )}

      {/* ── Section 6: Legal Footer ── */}
      <footer
        data-testid="legal-footer"
        className="bg-black py-10 px-6 sm:px-12 border-t border-white/[0.05]"
      >
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-xs text-slate-500 font-light">
            © 2026 All the Rage Ventures, LLC. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            <button
              onClick={() => setActiveModal('privacy')}
              className="text-xs text-slate-500 font-light hover:text-white transition-colors duration-300"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => setActiveModal('terms')}
              className="text-xs text-slate-500 font-light hover:text-white transition-colors duration-300"
            >
              Terms of Service
            </button>
            <button
              onClick={() => navigate('community', '/community')}
              className="text-xs text-slate-500 font-light hover:text-white transition-colors duration-300 text-left"
            >
              Community Partnerships
            </button>
            <a
              href="mailto:hello@nodeworkspaces.com"
              data-testid="footer-email-link"
              className="text-xs text-slate-500 font-light hover:text-white transition-colors duration-300"
            >
              Questions? hello@nodeworkspaces.com
            </a>
          </div>
        </div>
      </footer>

      {/* ── Legal Modal Overlay ── */}
      {activeModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm cursor-pointer transition-opacity"
            onClick={() => setActiveModal(null)}
          />

          {/* Modal Content Box */}
          <div className="relative w-full max-w-3xl max-h-[85vh] overflow-y-auto bg-[#121216] border border-white/10 rounded-2xl p-8 sm:p-12 shadow-2xl">
            <button
              onClick={() => setActiveModal(null)}
              className="absolute top-6 right-6 p-2 text-slate-500 hover:text-white bg-white/5 hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-white mb-8">
              {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
            </h2>

            <div className="text-slate-300 font-light text-sm leading-relaxed space-y-6">
              <p className="text-[#D4AF37] font-medium tracking-wide text-xs uppercase mb-8">Last Updated: April 2026</p>

              {activeModal === 'privacy' ? (
                <>
                  <div>
                    <h3 className="text-white font-medium mb-2 text-base">1. Information We Collect</h3>
                    <p>We collect information you provide directly to us when you join our waitlist or apply for a Community Grant. This may include your name, email address, and organizational affiliation.</p>
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2 text-base">2. How We Use Your Information</h3>
                    <p className="mb-2">We use the information we collect to:</p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Notify you when Node Workspaces officially opens.</li>
                      <li>Deliver your 50% off early-access promotional credit.</li>
                      <li>Review and coordinate Community Grant applications.</li>
                      <li>Send technical notices, updates, and administrative messages.</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2 text-base">3. Data Storage and Third Parties</h3>
                    <p>We do not sell, rent, or trade your personal information. Your data is stored securely using standard third-party infrastructure (such as Google Workspace) strictly for our internal operational use.</p>
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2 text-base">4. Contact Us</h3>
                    <p>If you have any questions about this Privacy Policy, please contact us at hello@nodeworkspaces.com. Node Workspaces is operated by All the Rage Ventures, LLC.</p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <h3 className="text-white font-medium mb-2 text-base">1. Acceptance of Terms</h3>
                    <p>By accessing the Node Workspaces website and joining our waitlist, you agree to be bound by these Terms of Service. Node Workspaces is a commercial brand operated by All the Rage Ventures, LLC.</p>
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2 text-base">2. The Waitlist Promotion</h3>
                    <p className="mb-2">Users who successfully submit their email address to the Early Access waitlist are eligible for a "50% Off" promotional credit. This credit is:</p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Valid for your single first booking only (subject to a maximum hour limit to be defined upon launch).</li>
                      <li>Non-transferable and holds no cash value.</li>
                      <li>Subject to physical availability of the workspace.</li>
                      <li>We reserve the right to modify, suspend, or terminate this promotion at any time.</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2 text-base">3. Physical Workspace Guidelines</h3>
                    <p>Joining the waitlist or applying for a Community Grant does not guarantee access to the physical space. All future bookings will be subject to our standard Facility Use Agreement, which governs acoustic privacy standards, unstaffed access protocols, and liability within the physical micro-offices.</p>
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2 text-base">4. Limitation of Liability</h3>
                    <p>To the fullest extent permitted by applicable law, All the Rage Ventures, LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of this promotional website or waitlist.</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}